import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { getShapeBounds } from '../hooks/useShapeResize';
import { computeRotationTransform } from '../utils/geometry';
const HANDLE_RADIUS_PX = 6;
// Rotation dot constants
const ROTATE_DOT_OFFSET_PX = 20;
const ROTATE_DOT_RADIUS_PX = 4;
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CORNER_HANDLES = ['nw', 'ne', 'se', 'sw'];
function getHandlePosition(handle, bounds) {
    const { x, y, width, height } = bounds;
    switch (handle) {
        case 'nw':
            return { x, y };
        case 'n':
            return { x: x + width / 2, y };
        case 'ne':
            return { x: x + width, y };
        case 'e':
            return { x: x + width, y: y + height / 2 };
        case 'se':
            return { x: x + width, y: y + height };
        case 's':
            return { x: x + width / 2, y: y + height };
        case 'sw':
            return { x, y: y + height };
        case 'w':
            return { x, y: y + height / 2 };
    }
}
/**
 * Purely visual selection outline with handles and rotation dots.
 * All pointer-event handling (resize, rotate, move) is done via JS
 * coordinate math in AnnotationEditor's handlePointerDown, bypassing
 * Firefox's broken SVG hit testing with preserveAspectRatio="none".
 *
 * containerWidth/containerHeight should be the actual screen pixel
 * dimensions (from getBoundingClientRect), so handle sizes are
 * constant on screen regardless of zoom method.
 */
export const SelectionOutline = React.memo(function SelectionOutline({ shape, containerWidth, containerHeight, showHandles = true, }) {
    const bounds = getShapeBounds(shape);
    // Compute radii so handles appear as perfect circles on screen.
    // containerWidth/Height are actual screen pixels, so dividing
    // pixel constants by them gives correct normalized SVG coords.
    const rx = containerWidth > 0 ? HANDLE_RADIUS_PX / containerWidth : 0.008;
    const ry = containerHeight > 0 ? HANDLE_RADIUS_PX / containerHeight : 0.008;
    // Rotation dot offset and radii in normalized coords
    const dotOffsetX = containerWidth > 0 ? ROTATE_DOT_OFFSET_PX / containerWidth : 0.02;
    const dotOffsetY = containerHeight > 0 ? ROTATE_DOT_OFFSET_PX / containerHeight : 0.02;
    const dotRx = containerWidth > 0 ? ROTATE_DOT_RADIUS_PX / containerWidth : 0.005;
    const dotRy = containerHeight > 0 ? ROTATE_DOT_RADIUS_PX / containerHeight : 0.005;
    // Rotation transform for visual elements (SVG matrix for correct rendering)
    const rotation = shape.rotation ?? 0;
    const rotationTransform = rotation
        ? computeRotationTransform(rotation, bounds.x + bounds.width / 2, bounds.y + bounds.height / 2, containerWidth, containerHeight)
        : undefined;
    return (_jsx("g", { className: "hekate-annotation-selection", "data-selection-for": shape.id, "data-shape-id": shape.id, pointerEvents: "none", children: _jsxs("g", { transform: rotationTransform, children: [_jsx("rect", { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height, fill: "none", stroke: "#0750B2", strokeWidth: 2, strokeDasharray: "6 6", vectorEffect: "non-scaling-stroke" }), showHandles &&
                    (bounds.width * containerWidth > 30 && bounds.height * containerHeight > 30) &&
                    CORNER_HANDLES.map((corner) => {
                        const pos = getHandlePosition(corner, bounds);
                        const dirX = corner.includes('e') ? 1 : -1;
                        const dirY = corner.includes('s') ? 1 : -1;
                        return (_jsx("ellipse", { cx: pos.x + dotOffsetX * dirX, cy: pos.y + dotOffsetY * dirY, rx: dotRx, ry: dotRy, fill: "#7EC8FF", stroke: "#0750B2", strokeWidth: 1, vectorEffect: "non-scaling-stroke" }, `dot-vis-${corner}`));
                    }), showHandles &&
                    HANDLES.map((handle) => {
                        const pos = getHandlePosition(handle, bounds);
                        return (_jsx("ellipse", { "data-handle": handle, cx: pos.x, cy: pos.y, rx: rx, ry: ry, fill: "#7EC8FF", stroke: "#0750B2", strokeWidth: 1, vectorEffect: "non-scaling-stroke" }, `handle-vis-${handle}`));
                    })] }) }));
});
